import {useRef, useState} from 'react';
import cx from 'classnames';
import {NextPage} from 'next';
import NextLink from 'next/link';
import {useRouter} from 'next/router';

import {useMyTallyAgeQuery} from '#apollo/schema';
import {Button, Icon, Link, Screen, Text} from '#component/base';
import {ModalRoot, ModalTrigger} from '#component/base/Modal';
import {HistoryWelcomeModal} from '#component/modal';
import {DeltaTab, LifestyleTab} from '#component/page/MyTallyAge/component';
import {Hero, TestResultListItem} from '#component/partial';
import {Transition} from '#component/util';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {useViewportSize} from '#hook';
import {pageSlideInKeyframe} from '#style/keyframes.css';
import {sprinkles} from '#style/sprinkles';
import {getLifestyleScoreTitle} from '#util/lifestyleScore';

import {EmptyHistory} from './component/EmptyHistory';
import {MyTallyAgeCTACard} from './component/MyTallyAgeCTACard/MyTallyAgeCTACard';
import {getHistoryListItems, getRenderedHeader} from './MyTallyAge.util';

import * as css from './MyTallyAge.css';

const MyTallyAgeScreen = () => {
  const {isDesktop, isTablet} = useViewportSize();
  const {push} = useRouter();
  const [modalOpen, setModalOpen] = useState(false);
  const startDate = useRef(new Date(2023, 2, 23)); // Tally launch date
  const endDate = useRef(new Date()); // Today
  const {data, loading} = useMyTallyAgeQuery({
    variables: {
      input: {
        endDate: endDate.current,
        startDate: startDate.current,
      },
    },
    onCompleted: ({me}) => {
      const labSubmissionWithResult = me?.mostRecentLabSubmissionWithResults;

      // When the user has a `labSubmission` with results, they should be
      // redirected to `/results` if:
      if (labSubmissionWithResult) {
        const {labRecord} = labSubmissionWithResult;

        // ... there is no associated `labRecord`
        if (!labRecord) {
          return push('/results');
        }

        // ... or, the `labRecord` indicates they have not viewed the
        // results.
        if (labRecord && !labRecord.hasViewedResultStory) {
          return push('/results');
        }
      }

      if (!!me && !me.hasViewedResultsHistory) {
        setModalOpen(true);
      }
    },
  });
  const {me, userTrends} = data || {};
  const {
    hasViewedResultsHistory,
    labSubmissions,
    mostRecentLabSubmission,
    mostRecentLabSubmissionWithResults,
    subscription,
  } = me || {};
  const history = getHistoryListItems(labSubmissions);
  const renderedHeader = getRenderedHeader({
    mostRecentLabSubmission,
    mostRecentLabSubmissionWithResults,
  });
  const [userTrend] = userTrends || [];
  const {lifestyleScorePercentage} = userTrend || {};

  const isMember = subscription?.hasMembership;
  const hasSurveyAssessment = !!mostRecentLabSubmission?.userSurveySubmission?.surveyAssessment?.id;
  const hasLabSubmission = !!mostRecentLabSubmission?.id;
  const showTakeLifestyleSurvey = !hasSurveyAssessment && hasLabSubmission;

  const lifestyleSurveyImageUrl = isDesktop
    ? '/image/action-plan/take-a-lifestyle-survey.jpg'
    : '/image/action-plan/take-a-lifestyle-survey-mobile.jpg';
  let lifestyleSurveyImagePosition: string;

  if (isDesktop) {
    lifestyleSurveyImagePosition = 'center';
  } else if (isTablet) {
    lifestyleSurveyImagePosition = '0 15%';
  } else {
    lifestyleSurveyImagePosition = 'top';
  }

  return (
    <Screen
      pageName="My TallyAge"
      className={sprinkles({bg: 'ecruWhite'})}
      headerProps={{variant: 'dark'}}
      withDefaultSizing={false}
      loading={loading}>
      <Hero showOverlap={false}>
        <Transition
          appear
          className={cx(
            sprinkles({layout: 'grid', items: 'center', pB: [80, 128, 192], pT: [80, 128]}),
            pageSlideInKeyframe,
          )}
          show>
          {renderedHeader}
        </Transition>
      </Hero>

      <div
        className={cx(
          sprinkles({
            layout: 'flex',
            dir: 'col',
            pY: [32, 56, 80],
          }),
          pageSlideInKeyframe,
        )}>
        <div
          className={sprinkles({
            flex: 'flex',
            dir: ['col', 'col', 'row'],
            w: 'full',
            gap: 24,
            content: ['center'],
            items: ['start'],
          })}>
          <div>
            <div
              className={sprinkles({
                display: 'flex',
                items: ['start', 'center'],
                mB: 8,
                dir: ['col', 'row'],
                w: 'full',
              })}>
              <Icon
                name="ChartLine"
                weight="thin"
                color="burgundy"
                className={sprinkles({
                  mR: 20,
                  size: [48, 64],
                })}
              />
              <Text as="h2" color="deepBurgundy" variant="heading2">
                Results History
              </Text>
            </div>
            <Text as="p" color="doveGray">
              Review your past results and start to highlight trends and relationships across your
              TallyAge™ Delta and Lifestyle. To access your Lifestyle Summary, you must first take a
              Lifestyle Survey to get accurate results.
            </Text>
          </div>

          {!!history.length && (
            <ModalRoot open={modalOpen} onOpenChange={setModalOpen}>
              <HistoryWelcomeModal
                hasViewedResultsHistory={hasViewedResultsHistory}
                // Set a `key` to reset modal's internal state when re-opened.
                key={modalOpen ? 'opened' : 'closed'}
              />

              <Text
                className={sprinkles({
                  wrap: 'nowrap',
                  width: 'full',
                  display: 'flex',
                  items: ['center', 'start'],
                  placeContent: ['start', 'start', 'end'],
                })}>
                <Link as={ModalTrigger} className={sprinkles({wrap: 'nowrap'})}>
                  Understanding My Results
                </Link>
                <Icon
                  name="Info"
                  weight="thin"
                  color="doveGray"
                  className={sprinkles({
                    mL: 12,
                    size: [32, 32],
                  })}
                />
              </Text>
            </ModalRoot>
          )}
        </div>

        {showTakeLifestyleSurvey && (
          <NextLink href="/survey" passHref>
            <Button
              as="a"
              className={sprinkles({w: ['full', 'full', 'maxContent'], mT: 48})}
              icon="ArrowRight"
              variant="primary">
              <Text color="inherit" variant="button">
                Take a Lifestyle Survey
              </Text>
            </Button>
          </NextLink>
        )}

        {history.length > 0 ? (
          <div className={css.content}>
            <div className={css.scrollableContainer}>
              <div className={css.scrollable}>
                <div className={css.lifestyleGraph}>
                  <LifestyleTab
                    showTakeLifestyleSurvey={showTakeLifestyleSurvey}
                    userTrends={userTrends}
                    lifestyleHealthiness={
                      !!lifestyleScorePercentage
                        ? getLifestyleScoreTitle(lifestyleScorePercentage)
                        : ''
                    }
                  />
                </div>
                <div className={css.deltaGraph}>
                  <DeltaTab userTrends={userTrends} />
                </div>
              </div>
            </div>
            <div className={css.resultsHistory}>
              <ul
                className={sprinkles({
                  colSpan: [4, 6, 5],
                  colStart: [1, 2, 6],
                  dir: 'col',
                  flex: 'flex',
                  gap: 12,
                })}>
                {history.length <= 0 && (
                  <li>
                    <TestResultListItem />
                  </li>
                )}
                {history.map(({id, ...otherProps}) => (
                  <li key={id}>
                    <TestResultListItem {...otherProps} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className={sprinkles({display: 'flex', paddingTop: 64, paddingBottom: [64, 192]})}>
            <EmptyHistory />
          </div>
        )}
        {!isMember && !showTakeLifestyleSurvey && (
          <MyTallyAgeCTACard
            headline="Become a member"
            href="/survey"
            imageUrl="/image/recover-password.jpg"
            lede="Become a Tally Health™ member today, discover your epigenetic age, and get science-backed tools to unlock additional years of health and vitality."
            variant="secondary"
          />
        )}
        {showTakeLifestyleSurvey && (
          <MyTallyAgeCTACard
            headline="Take a Lifestyle Survey"
            href="/survey"
            imageUrl={lifestyleSurveyImageUrl}
            objectPosition={lifestyleSurveyImagePosition}
            lede="We’re here to guide you to improved health, vibrancy, and longevity. Our Lifestyle Survey may help you better understand habits that influence aging."
          />
        )}
      </div>
    </Screen>
  );
};

export const MyTallyAge: NextPage = withRequireUserState(MyTallyAgeScreen, {userState: 'verified'});
